import { useState } from 'react';
import FileUpload from 'components/FileUpload';
import DownloadBox from 'components/DownloadBox';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import { toolAPI, mkUrl } from 'utils/api';

export default function RemoveBg() {
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [dlUrl, setDlUrl]     = useState('');

  const handleFile = f => {
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : '');
    setDlUrl(''); setError('');
  };

  const reset = () => { setFile(null); setPreview(''); setError(''); setDlUrl(''); };

  const handle = async () => {
    if (!file) { setError('Please select an image file.'); return; }
    setLoading(true); setError(''); setDlUrl('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await toolAPI.removeBg(fd);
      setDlUrl(mkUrl(res.data));
    } catch (err) {
      setError(err.response?.data?.error || 'Background removal failed. Try an image with a clear, solid-colored background.');
    } finally { setLoading(false); }
  };

  return (
    <>
    <Seo title="Remove Image Background Free" description="Remove the background from any image automatically. Free, no signup." path="/tools/image/remove-bg" />
    <div className="tool-pg">
      <div className="container" style={{ maxWidth: 780 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon"><Icon name="eraser" size={24} /></div>
            <div>
              <h1>Remove Background</h1>
              <p>Remove the background from any image automatically. Download as transparent PNG.</p>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { icon: 'upload',   step: '1', title: 'Upload Image',   desc: 'JPG, PNG or WEBP' },
            { icon: 'eraser',   step: '2', title: 'Auto Processing', desc: 'Background detected & removed' },
            { icon: 'download', step: '3', title: 'Download PNG',   desc: 'Transparent background' },
          ].map(s => (
            <div key={s.title} style={{ background: 'var(--white)', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r)', padding: '14px 12px', textAlign: 'center' }}>
              <div style={{ width: 34, height: 34, background: 'var(--blue-50)', borderRadius: 'var(--r-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', color: 'var(--blue)' }}>
                <Icon name={s.icon} size={16} />
              </div>
              <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 3 }}>{s.title}</p>
              <p style={{ fontSize: 12, color: 'var(--slate-400)', lineHeight: 1.4 }}>{s.desc}</p>
            </div>
          ))}
        </div>

        {!dlUrl ? (
          <div className="tool-box">
            <FileUpload
              accept="image/*"
              onFileChange={handleFile}
              label="Upload an image to remove background"
              hint="Best results: subject on solid/simple background · JPG, PNG, WEBP · Max 20MB"
            />

            {preview && (
              <div style={{ marginTop: 20 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--slate-400)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                  Original Image Preview
                </p>
                {/* Checkerboard background simulates transparency */}
                <div className="checker" style={{ padding: 16, display: 'flex', justifyContent: 'center', minHeight: 160 }}>
                  <img
                    src={preview}
                    alt="Preview"
                    style={{ maxHeight: 260, maxWidth: '100%', objectFit: 'contain', borderRadius: 6 }}
                  />
                </div>
                <p style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 8, textAlign: 'center' }}>
                  {file?.name} · {file ? (file.size / 1024).toFixed(0) : 0} KB
                </p>
              </div>
            )}

            {error && (
              <div className="alert alert-error" style={{ marginTop: 14 }}>
                <Icon name="alertCircle" size={16} />{error}
              </div>
            )}

            <button
              className="btn btn-primary btn-lg btn-full"
              style={{ marginTop: 20 }}
              onClick={handle}
              disabled={loading || !file}
            >
              {loading
                ? <><span className="spinner sp-sm" />Removing Background... (10–20 seconds)</>
                : <><Icon name="eraser" size={18} />Remove Background</>
              }
            </button>
          </div>
        ) : (
          <DownloadBox
            url={dlUrl}
            filename="background-removed.png"
            onReset={reset}
          />
        )}

        <div className="alert alert-info" style={{ marginTop: 14 }}>
          <Icon name="info" size={16} />
          <span>
            <strong>How it works:</strong> Uses a smart edge-detection algorithm to find and remove the background.
            Works best on images with a clear subject and solid/uniform background (white, blue, green, etc.).
            For professional AI-quality results on complex images, add a free{' '}
            <a href="https://www.remove.bg/api" target="_blank" rel="noreferrer" style={{ color: 'var(--blue)', fontWeight: 600 }}>remove.bg API key</a>
            {' '}(50 free/month) to server <code style={{ fontSize: 12, background: 'var(--slate-100)', padding: '1px 5px', borderRadius: 4 }}>.env</code>.
          </span>
        </div>
      </div>
    </div>
  </>
  );
}
